import React, {useEffect, useState} from "react";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {toastError} from "../../../../helpers/Toast";

const EmployeeAssetsCard = ({employeeId}) => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles) ? auth.roles : String(auth?.roles || "").replace(/[\[\]\s]/g, "").split(",");
  const allowed = roles.includes("ROLE_HR") || roles.includes("ROLE_ADMIN");
  const [deposits, setDeposits] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [deposit, setDeposit] = useState({itemName: "", unitAmount: "", quantity: 1, collectedOn: new Date().toISOString().slice(0, 10), notes: ""});
  const [voucher, setVoucher] = useState({quantity: 1, issuedOn: new Date().toISOString().slice(0, 10), consent: false, description: "", notes: ""});
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    if (!allowed || !employeeId) return;
    try {
      const [d, v] = await Promise.all([
        axiosPrivate.get(`/employee-deposits/employee/${employeeId}`),
        axiosPrivate.get(`/employee-vouchers/employee/${employeeId}`)
      ]);
      setDeposits(d.data || []);
      setVouchers(v.data || []);
    } catch (e) {
      toastError("Nie udało się pobrać kaucji i voucherów");
    }
  };

  useEffect(() => { load(); }, [employeeId, allowed]);

  const addDeposit = async () => {
    if (!deposit.itemName.trim() || !deposit.unitAmount) {
      toastError("Podaj przedmiot kaucji i kwotę");
      return;
    }
    try {
      await axiosPrivate.post("/employee-deposits", {...deposit, employeeId, unitAmount: Number(deposit.unitAmount), quantity: Number(deposit.quantity || 1)});
      setDeposit({itemName: "", unitAmount: "", quantity: 1, collectedOn: new Date().toISOString().slice(0, 10), notes: ""});
      await load();
    } catch (e) {
      toastError("Nie udało się zapisać kaucji");
    }
  };

  const returnDeposit = async item => {
    try {
      await axiosPrivate.put(`/employee-deposits/${item.id}`, {...item, returnedOn: new Date().toISOString().slice(0, 10)});
      await load();
    } catch (e) {
      toastError("Nie udało się oznaczyć zwrotu kaucji");
    }
  };

  const addVoucher = async () => {
    try {
      await axiosPrivate.post("/employee-vouchers", {...voucher, employeeId, quantity: Number(voucher.quantity || 1)});
      setVoucher({quantity: 1, issuedOn: new Date().toISOString().slice(0, 10), consent: false, description: "", notes: ""});
      await load();
    } catch (e) {
      toastError("Nie udało się zapisać voucherów");
    }
  };

  const exportMonthlyReport = async () => {
    const [year, month] = reportMonth.split("-").map(Number);
    if (!year || !month) {
      toastError("Wybierz miesiąc raportu");
      return;
    }
    try {
      const response = await axiosPrivate.get("/employee-assets/report.xlsx", {params: {year, month}, responseType: "blob"});
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rozliczenia_pracownicze_${reportMonth}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toastError("Nie udało się wygenerować raportu kaucji i voucherów");
    }
  };

  if (!allowed || !employeeId) return null;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <strong>Kaucje i vouchery</strong>
        <div className="d-flex align-items-center gap-2">
          <input type="month" className="form-control form-control-sm" value={reportMonth} onChange={e => setReportMonth(e.target.value)}/>
          <button type="button" className="btn btn-sm btn-outline-success text-nowrap" onClick={exportMonthlyReport}>Raport Excel</button>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-xl-7">
            <h6>Kaucje</h6>
            <div className="row g-2 mb-2">
              <div className="col-md-4"><input className="form-control" placeholder="Za co" value={deposit.itemName} onChange={e => setDeposit({...deposit, itemName: e.target.value})}/></div>
              <div className="col-md-2"><input type="number" min="0" step="0.01" className="form-control" placeholder="Kwota" value={deposit.unitAmount} onChange={e => setDeposit({...deposit, unitAmount: e.target.value})}/></div>
              <div className="col-md-2"><input type="number" min="1" className="form-control" value={deposit.quantity} onChange={e => setDeposit({...deposit, quantity: e.target.value})}/></div>
              <div className="col-md-3"><input type="date" className="form-control" value={deposit.collectedOn} onChange={e => setDeposit({...deposit, collectedOn: e.target.value})}/></div>
              <div className="col-md-1"><button type="button" className="btn btn-primary" onClick={addDeposit}>+</button></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm"><thead><tr><th>Przedmiot</th><th>Kwota</th><th>Pobrano</th><th>Pobrał</th><th>Zwrot</th><th>Zwrócił</th><th></th></tr></thead><tbody>
                {deposits.map(item => <tr key={item.id}><td>{item.itemName} × {item.quantity}</td><td>{item.totalAmount ?? item.unitAmount}</td><td>{item.collectedOn}</td><td>{item.collectedByName || "—"}</td><td>{item.returnedOn || "—"}</td><td>{item.returnedByName || "—"}</td><td>{!item.returnedOn && <button type="button" className="btn btn-sm btn-outline-success" onClick={() => returnDeposit(item)}>Zwrócono</button>}</td></tr>)}
                {!deposits.length && <tr><td colSpan="7" className="text-muted">Brak kaucji</td></tr>}
              </tbody></table>
            </div>
          </div>
          <div className="col-xl-5">
            <h6>Vouchery</h6>
            <div className="row g-2 mb-2">
              <div className="col-3"><input type="number" min="1" className="form-control" value={voucher.quantity} onChange={e => setVoucher({...voucher, quantity: e.target.value})}/></div>
              <div className="col-5"><input type="date" className="form-control" value={voucher.issuedOn} onChange={e => setVoucher({...voucher, issuedOn: e.target.value})}/></div>
              <div className="col-4"><button type="button" className="btn btn-primary w-100" onClick={addVoucher}>Dodaj</button></div>
              <div className="col-12"><input className="form-control" placeholder="Opis" value={voucher.description} onChange={e => setVoucher({...voucher, description: e.target.value})}/></div>
              <div className="col-12"><label className="form-check"><input type="checkbox" className="form-check-input" checked={voucher.consent} onChange={e => setVoucher({...voucher, consent: e.target.checked})}/><span className="form-check-label">Potwierdzona zgoda / odbiór</span></label></div>
            </div>
            <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Data</th><th>Ilość</th><th>Opis</th><th>Wydał</th><th>Zgoda</th></tr></thead><tbody>
              {vouchers.map(item => <tr key={item.id}><td>{item.issuedOn}</td><td>{item.quantity}</td><td>{item.description || "—"}</td><td>{item.issuedByName || "—"}</td><td>{item.consent ? "TAK" : "NIE"}</td></tr>)}
              {!vouchers.length && <tr><td colSpan="5" className="text-muted">Brak voucherów</td></tr>}
            </tbody></table></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAssetsCard;
